/**
 * Reports Pinia store — manages the list of generated reports, monthly stats,
 * 6-month trend, and the PDF download flow.
 *
 * State shape mirrors the backend DTOs from src/HoaWatch.Api/Contracts/ReportDto.cs.
 * All mutations go through actions; never mutate state outside this store.
 */
import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { listReports, generateMonthlyReport, getMonthlyStats, getReportsTrend, getReportDownloadUrl, } from '@/api/reports';
// ── Helpers ──────────────────────────────────────────────────────────────────
/**
 * Returns the current month in "yyyy-MM" format in the browser's local timezone.
 * The server always stores months in UTC "yyyy-MM" but for the UI we use local
 * time as the default generation target (the board is in the same timezone as the HOA).
 */
function currentMonthYyyyMm() {
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    return `${yyyy}-${mm}`;
}
/**
 * Human-readable file size (bytes → KB / MB).
 */
export function formatFileSize(bytes) {
    if (bytes < 1024)
        return `${bytes} B`;
    if (bytes < 1024 * 1024)
        return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}
// ── Store ────────────────────────────────────────────────────────────────────
export const useReportsStore = defineStore('reports', () => {
    // ── State ─────────────────────────────────────────────────────────────────
    const reports = ref([]);
    const monthlyStats = ref(null);
    const trend = ref(null);
    const loading = ref(false);
    const statsLoading = ref(false);
    const trendLoading = ref(false);
    const generating = ref(false);
    const downloading = ref(false);
    const error = ref(null);
    const statsError = ref(null);
    const trendError = ref(null);
    const generateError = ref(null);
    const downloadError = ref(null);
    /** Month currently displayed in the stats panel (yyyy-MM). */
    const selectedMonth = ref(currentMonthYyyyMm());
    // ── Computed ──────────────────────────────────────────────────────────────
    /** The most-recently generated report for the selected month, if any. */
    const reportForSelectedMonth = computed(() => reports.value.find(r => r.month === selectedMonth.value) ?? null);
    /** True if a report for the selected month already exists. */
    const hasReportForSelectedMonth = computed(() => reportForSelectedMonth.value !== null);
    // ── Actions ───────────────────────────────────────────────────────────────
    /**
     * Fetch the full list of past reports for the current tenant.
     */
    async function fetchReports() {
        loading.value = true;
        error.value = null;
        try {
            reports.value = await listReports();
        }
        catch (e) {
            error.value = e instanceof Error ? e.message : 'Failed to load reports.';
        }
        finally {
            loading.value = false;
        }
    }
    /**
     * Fetch monthly statistics for the given month (defaults to `selectedMonth`).
     */
    async function fetchMonthlyStats(month) {
        const target = month ?? selectedMonth.value;
        statsLoading.value = true;
        statsError.value = null;
        try {
            monthlyStats.value = await getMonthlyStats(target);
        }
        catch (e) {
            statsError.value = e instanceof Error ? e.message : 'Failed to load monthly stats.';
        }
        finally {
            statsLoading.value = false;
        }
    }
    /**
     * Fetch the trailing-6-month trend data.
     */
    async function fetchTrend(months = 6) {
        trendLoading.value = true;
        trendError.value = null;
        try {
            trend.value = await getReportsTrend(months);
        }
        catch (e) {
            trendError.value = e instanceof Error ? e.message : 'Failed to load trend data.';
        }
        finally {
            trendLoading.value = false;
        }
    }
    /**
     * Generate (or regenerate) the PDF for `selectedMonth`.
     * On success, refreshes the report list so the new row appears immediately.
     */
    async function generateReport(month) {
        const target = month ?? selectedMonth.value;
        generating.value = true;
        generateError.value = null;
        try {
            const newReport = await generateMonthlyReport(target);
            // Upsert into local list: remove any existing row for this month, prepend the new one.
            reports.value = [
                newReport,
                ...reports.value.filter(r => r.month !== newReport.month),
            ];
        }
        catch (e) {
            generateError.value = e instanceof Error ? e.message : 'Failed to generate report.';
        }
        finally {
            generating.value = false;
        }
    }
    /**
     * Obtain a 15-minute SAS URL for a report and trigger a browser download.
     * Opens the URL in a new tab — the browser's native PDF handling takes over.
     */
    async function downloadReport(id) {
        downloading.value = true;
        downloadError.value = null;
        try {
            const sasUrl = await getReportDownloadUrl(id);
            // Open in a new tab; the browser's PDF viewer (or download prompt) handles it.
            window.open(sasUrl, '_blank', 'noopener,noreferrer');
        }
        catch (e) {
            downloadError.value = e instanceof Error ? e.message : 'Failed to get download link.';
        }
        finally {
            downloading.value = false;
        }
    }
    /**
     * Change the active month and re-fetch stats automatically.
     */
    async function setSelectedMonth(month) {
        selectedMonth.value = month;
        await fetchMonthlyStats(month);
    }
    /**
     * Load all data needed by the Reports view in parallel.
     */
    async function initReportsView() {
        await Promise.all([
            fetchReports(),
            fetchMonthlyStats(),
            fetchTrend(),
        ]);
    }
    // ── Reset ─────────────────────────────────────────────────────────────────
    function $reset() {
        reports.value = [];
        monthlyStats.value = null;
        trend.value = null;
        loading.value = false;
        statsLoading.value = false;
        trendLoading.value = false;
        generating.value = false;
        downloading.value = false;
        error.value = null;
        statsError.value = null;
        trendError.value = null;
        generateError.value = null;
        downloadError.value = null;
        selectedMonth.value = currentMonthYyyyMm();
    }
    return {
        // state (readonly refs for consumers)
        reports,
        monthlyStats,
        trend,
        loading,
        statsLoading,
        trendLoading,
        generating,
        downloading,
        error,
        statsError,
        trendError,
        generateError,
        downloadError,
        selectedMonth,
        // computed
        reportForSelectedMonth,
        hasReportForSelectedMonth,
        // actions
        fetchReports,
        fetchMonthlyStats,
        fetchTrend,
        generateReport,
        downloadReport,
        setSelectedMonth,
        initReportsView,
        $reset,
    };
});
